<template>
  <validation-observer ref="refFormObserver">
    <b-form>
      <b-row class="mx-0">
        <b-col cols="6">
          <!-- delayed_record_minutes -->
          <validation-provider
            #default="validationContext"
            name="delayed_record_minutes"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera.previous_time_of_prealarma')"
              label-for="delayed_record_minutes"
            >
              <flat-pickr
                id="delayed_record_minutes"
                class="form-control"
                :state="getValidationState(validationContext)"
                :config="{
                  enableTime: true,
                  dateFormat: 'H:i:S',
                  time_24hr: true,
                  noCalendar: true,
                  enableSeconds: true,
                }"
                v-model="itemData.delayed_record_minutes"
              />
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col cols="6">
          <!-- forward_record_minutes -->
          <validation-provider
            #default="validationContext"
            name="forward_record_minutes"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera.later_time_of_prealarma')"
              label-for="forward_record_minutes"
            >
              <flat-pickr
                id="forward_record_minutes"
                class="form-control"
                :state="getValidationState(validationContext)"
                :config="{
                  enableTime: true,
                  dateFormat: 'H:i:S',
                  time_24hr: true,
                  noCalendar: true,
                  enableSeconds: true,
                }"
                v-model="itemData.forward_record_minutes"
              />
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>
      <b-row class="mx-0">
        <b-col cols="6">
          <!-- rec_mode -->
          <validation-provider
            #default="validationContext"
            name="rec_mode"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera_proxy.rec_mode')"
              label-for="rec_mode"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.rec_mode"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="recsModeOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="rec_mode"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- RTSP Available -->
          <validation-provider name="rtsp_available" rules="required" vid="rtsp_available">
            <b-form-checkbox
              id="rtsp_available"
              v-model="itemData.rtsp_available"
              name="rtsp_available"
              value="1"
              class="my-1 text-capitalize"
              unchecked-value="0"
              switch
            >
              {{ $t("camera_proxy.rtsp_available") }}
            </b-form-checkbox>
          </validation-provider>
        </b-col>
        <b-col cols="6">
          <!-- limits (Recording hours) -->
          <validation-provider
            #default="validationContext"
            name="rec_hours"
            v-show="itemData.rec_mode !== 'off'"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('camera_proxy.rec_hours')"
              label-for="rec_hours"
              :state="getValidationState(validationContext)"
            >
              <div class="container-btns-limit">
                <button
                  v-for="limit in limits"
                  :class="[
                    'btn-limits-normal',
                    { 'btn-limits-selected': localSelectedLimit === limit.value }
                  ]"
                  :key="limit.value"
                  :id="'btn-' + limit.value"
                  @click="selectLimit(limit.value)"
                  type="button"
                >
                  {{ $t("camera_proxy." + limit.label) }}
                </button>
              </div>
              <div v-if="localSelectedLimit === 0" class="mt-2">
                <b-form-group class="text-capitalize" label-for="limitPer">
                  <div class="input-wrapper">
                    <b-input
                      id="limitPer"
                      v-model="customDays"
                      type="number"
                      class="input-days"
                      @input="updateCustomDays"
                    />
                    <span>{{ $t("camera_proxy.days") }}</span>
                  </div>
                </b-form-group>
              </div>
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<style lang="scss" scoped>
.btn-limits-selected {
  background-color: #4fa9ca;
  color: white;
  border-color: #4fa9ca;
}

.input-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.input-days {
  max-width: 120px;
}

.days-label {
  font-weight: 500;
}
</style>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";
import vSelect from "vue-select";
import SelectPaginate from "@/views/components/SelectPaginate.vue";
import { ref, watch, onMounted, nextTick } from "@vue/composition-api";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import flatPickr from "vue-flatpickr-component";

export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    SelectPaginate,
    flatPickr,
  },
  props: {
    propItemData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isAdd: {
      type: Boolean,
      required: false,
      default: false,
    },
    selectedLimit: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const { refFormObserver, getValidationState } = formValidation();
    const itemData = ref(JSON.parse(JSON.stringify(props.propItemData)));

    const localSelectedLimit = ref(props.selectedLimit);
    const customDays = ref(0);
    const isCustomInput = ref(false);

    const recsModeOptions = [
      { value: "off", label: "Off" },
      { value: "on", label: "On" },
      { value: "by_event", label: "By event" },
    ];
    const limits = [
      { label: "limit.3_days", value: 72 },
      { label: "limit.7_days", value: 168 },
      { label: "limit.14_days", value: 336 },
      { label: "limit.30_days", value: 720 },
      { label: "limit.60_days", value: 1440 },
      { label: "limit.90_days", value: 2160 },
      { label: "limit.180_days", value: 4320 },
      { label: "limit.1_year", value: 8760 },
      { label: "limit.2_years", value: 17520 },
      { label: "limit.custom", value: 0 },
    ];

    const selectLimit = (value) => {
      localSelectedLimit.value = value;
      if (value === 0) {
        customDays.value = hoursToDays(itemData.value.rec_limit || 0);
      } else {
        itemData.value.rec_limit = value;
      }
    };

    const btnlimit = (value) => {
      if (!value || isCustomInput.value) return;

      const hours = parseInt(value);
      const exactLimit = limits.find(limit => limit.value === hours);

      if (exactLimit) {
        localSelectedLimit.value = exactLimit.value;
      } else {
        localSelectedLimit.value = 0;
        if (!isCustomInput.value) {
          customDays.value = hoursToDays(hours);
        }
      }
      itemData.value.rec_limit = hours;
    };

    const updateLimit = () => {
      this.itemData.rec_limit = this.daysToHours(this.customDays);
    };

    const hoursToDays = (data) => {
      if (data == null) {
        return 0;
      }
      return Math.round(data / 24);
    };
    const daysToHours = (data) => {
      return (parseInt(data) || 0) * 24;
    };

    const validateStep = () => {
      return refFormObserver.value.validate();
    };

    const setupWatcher = (itemData, key) => {
      watch(
        () => itemData.value[key],
        (newValue, oldValue) => {
          if (newValue !== oldValue) {
            emit("update:itemData", { [key]: newValue });
          }
        }
      );
    };

    // Add watchers for all recording-related fields
    setupWatcher(itemData, "delayed_record_minutes");
    setupWatcher(itemData, "forward_record_minutes");
    setupWatcher(itemData, "rec_mode");
    setupWatcher(itemData, "rec_limit");
    setupWatcher(itemData, "rtsp_available");

    watch(
      () => props.propItemData,
      (newValue) => {
        itemData.value = { ...newValue };
      },
      { deep: true }
    );

    watch(
      () => itemData.value.rec_limit,
      (newValue) => {
        if (!isCustomInput.value && newValue) {
          btnlimit(newValue);
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      if (itemData.value.rec_limit) {
        btnlimit(itemData.value.rec_limit);
      }
    });

    const updateCustomDays = (value) => {
      isCustomInput.value = true;
      const days = parseInt(value) || 0;
      customDays.value = days;
      const hours = daysToHours(days);
      itemData.value.rec_limit = hours;
    };

    return {
      t,
      refFormObserver,
      getValidationState,
      validateStep,
      itemData,
      recsModeOptions,
      limits,
      selectLimit,
      updateLimit,
      hoursToDays,
      daysToHours,
      btnlimit,
      localSelectedLimit,
      customDays,
      updateCustomDays,
      isCustomInput,
    };
  },
};
</script>
